/**
 * CLE-3081: how many installments a "Set amount" (InstallmentAmount) payment plan is made of. Mirrors
 * services/clear-api/src/utils/createInstallments.ts (the SPA cannot import it) — change both together.
 */

/**
 * A leftover smaller than 10% of the set installment amount is folded into the last full installment instead of
 * becoming its own tiny installment (843.87 / 120.55 is 7 installments, the last $120.57, not 8 with a $0.02 one).
 */
export const PLAN_REMAINDER_FOLD_RATIO = 0.1;

/** The most installments a set-amount plan can span; when capped, the last installment absorbs the rest. */
export const MAX_PAYMENT_PLAN_DURATION = 50;

/**
 * The installment count before the cap: ceil(remaining / amount), less one when the leftover folds. Only for
 * validation that must reject an amount too small to fit in MAX_PAYMENT_PLAN_DURATION payments. 0 when remaining <= 0
 * or the amount is not positive.
 */
export const setAmountInstallmentCountBeforeCap = (
  remainingInCents: number,
  amountInCents: number
): number => {
  if (!(amountInCents > 0) || !(remainingInCents > 0)) return 0;
  let count = Math.ceil(remainingInCents / amountInCents);
  const leftover = remainingInCents - (count - 1) * amountInCents;
  if (count > 1 && leftover < amountInCents * PLAN_REMAINDER_FOLD_RATIO) {
    count -= 1;
  }
  return count;
};

/**
 * The number of installments the API creates for a set-amount plan: the fold rule, then the cap. 0 when remaining <= 0
 * or the amount is not positive, at least 1 otherwise.
 */
export const setAmountInstallmentCount = (
  remainingInCents: number,
  amountInCents: number
): number =>
  Math.min(
    setAmountInstallmentCountBeforeCap(remainingInCents, amountInCents),
    MAX_PAYMENT_PLAN_DURATION
  );
